import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Upload,
    FileDown,
    AlertTriangle,
    CheckCircle,
    XCircle,
    FileSpreadsheet,
    Loader2,
    ShieldCheck,
    RefreshCw,
    Cpu,
    StopCircle,
    Search,
    Eye,
    Trash2,
    Sliders,
    Sparkles
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { BASE_URL } from '../utils/apiClient';

const TEMPLATE_HEADERS = [
    'Ad *',
    'Soyad *',
    'TCKN *',
    'E-Posta',
    'Telefon',
    'Doğum Tarihi',
    'İşe Giriş Tarihi *',
    'Departman *',
    'Pozisyon *',
    'Rol',
    'Sicil No',
    'Cinsiyet',
    'Lokasyon',
    'Çalışma Rejimi',
    'Kan Grubu',
    'Kart Numarası',
    'Yetki Grubu'
];

export default function EmployeeImportPage() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [parsingPreview, setParsingPreview] = useState(false);

    // Kurumsal aktarım seçenekleri
    const [updateExisting, setUpdateExisting] = useState(false);
    const [createUserAccounts, setCreateUserAccounts] = useState(true);
    const [mustChangePassword, setMustChangePassword] = useState(true);
    const [syncToDevices, setSyncToDevices] = useState(true);

    // Boş / "Belirtilmedi" hücreler için varsayılan seçimler
    const [lookups, setLookups] = useState({
        roles: [],
        accessGroups: [],
        workingRegimes: [],
        locations: []
    });
    const [defaultRoleName, setDefaultRoleName] = useState('');
    const [defaultAccessGroupId, setDefaultAccessGroupId] = useState('');
    const [defaultWorkingRegimeId, setDefaultWorkingRegimeId] = useState('');
    const [defaultLocationId, setDefaultLocationId] = useState('');

    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [downloadingErrorsExcel, setDownloadingErrorsExcel] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(null);
    const [errorSearch, setErrorSearch] = useState('');
    const pollRef = useRef(null);

    const authHeaders = () => {
        const token = localStorage.getItem('authToken');
        const firmCode = localStorage.getItem('firmCode') || sessionStorage.getItem('firmCode') || '';
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (firmCode) headers['X-Firm-Code'] = firmCode;
        return headers;
    };

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const applyStatus = (data) => {
        setProgress(data);
        if (!data?.isRunning) {
            stopPolling();
            setUploading(false);
            setCancelling(false);
            if (data?.status === 'Completed' || data?.status === 'Failed' || data?.status === 'Cancelled') {
                setResult({
                    runId: data.runId,
                    status: data.status,
                    totalRows: data.totalRows ?? 0,
                    successCount: data.successCount ?? 0,
                    createdCount: data.createdCount ?? data.successCount ?? 0,
                    UpdatedCount: data.updatedCount ?? 0,
                    skippedCount: data.skippedCount ?? 0,
                    failedCount: data.failedCount ?? 0,
                    deviceCommandsQueued: data.deviceCommandsQueued ?? 0,
                    totalErrorCount: data.totalErrorCount ?? (data.errors?.length || 0),
                    errors: data.errors || [],
                    detailedErrors: data.detailedErrors || []
                });
                if (data.status === 'Completed' && (data.failedCount ?? 0) === 0) {
                    setFile(null);
                    setPreview(null);
                }
                if (data.status === 'Failed' && data.message) {
                    setError(data.message);
                }
            }
        }
    };

    const startPolling = (runId) => {
        stopPolling();
        const tick = async () => {
            try {
                const res = await fetch(`${BASE_URL}/Employee/import/excel/status/${runId}`, {
                    headers: authHeaders()
                });
                if (!res.ok) return;
                const data = await res.json();
                applyStatus(data);
            } catch (e) {
                console.error(e);
            }
        };
        tick();
        pollRef.current = setInterval(tick, 1000);
    };

    useEffect(() => {
        // 1) Sistemdeki Rol, Yetki Grubu, Çalışma Rejimi ve Lokasyon tanımlarını çek
        (async () => {
            try {
                const res = await fetch(`${BASE_URL}/Employee/import/excel/lookups`, {
                    headers: authHeaders()
                });
                if (res.ok) {
                    const data = await res.json();
                    setLookups({
                        roles: data.roles || [],
                        accessGroups: data.accessGroups || [],
                        workingRegimes: data.workingRegimes || [],
                        locations: data.locations || []
                    });
                }
            } catch (e) {
                console.warn('Aktarım tanımları yüklenemedi:', e);
            }
        })();

        // 2) Devam eden aktif bir aktarım varsa durumunu yakala
        (async () => {
            try {
                const res = await fetch(`${BASE_URL}/Employee/import/excel/active`, {
                    headers: authHeaders()
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data?.isRunning && data.runId) {
                    setUploading(true);
                    applyStatus(data);
                    startPolling(data.runId);
                }
            } catch {
                /* ignore */
            }
        })();
        return () => stopPolling();
    }, []);

    // Sunucudan Açılır Listeli (Data Validation) Akıllı Excel Şablonu İndir
    const downloadTemplate = async () => {
        setDownloadingTemplate(true);
        try {
            const res = await fetch(`${BASE_URL}/Employee/import/excel/template`, {
                headers: authHeaders()
            });
            if (!res.ok) throw new Error('Sunucu şablonu oluşturulamadı');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `PDKS_Akilli_Personel_Aktarim_Sablonu_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.warn('Sunucu şablonu indirilemedi, yerel şablon oluşturuluyor:', err);
            const sampleData = [
                [
                    'Ahmet',
                    'Yılmaz',
                    '11111111111',
                    'ahmet.yilmaz@ornek.com',
                    '5551234567',
                    '1990-01-01',
                    '2023-01-01',
                    'Yazılım',
                    'Geliştirici',
                    'Personel',
                    '1001',
                    'Erkek',
                    'Belirtilmedi',
                    'Belirtilmedi',
                    'A+',
                    '1234567890',
                    'Belirtilmedi'
                ]
            ];

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...sampleData]);
            ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 22 }));
            XLSX.utils.book_append_sheet(wb, ws, 'Personel Listesi');
            XLSX.writeFile(wb, 'Personel_Aktarim_Sablonu.xlsx');
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const inspectExcelFile = async (selectedFile) => {
        if (!selectedFile) return;
        const ext = selectedFile.name.split('.').pop()?.toLowerCase();
        if (ext === 'xls') {
            setError(
                "Eski '.xls' (Excel 97-2003) formatı desteklenmemektedir. Lütfen dosyanızı Excel'de '.xlsx (Excel Çalışma Kitabı)' olarak kaydedip yükleyin."
            );
            setPreview(null);
            return;
        }

        setParsingPreview(true);
        try {
            const buffer = await selectedFile.arrayBuffer();
            // Büyük dosyalarda (20-30 bin satır) tarayıcıyı yormamak için sadece ilk 6 satırı parse et
            const wb = XLSX.read(buffer, { type: 'array', sheetRows: 6, cellDates: true });
            const firstSheetName = wb.SheetNames[0];
            const ws = wb.Sheets[firstSheetName];
            if (!ws) {
                setPreview(null);
                return;
            }

            const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
            const headerRow = rows[0] || [];
            const sampleRows = rows.slice(1, 6).filter((r) => r.some((cell) => String(cell).trim() !== ''));

            setPreview({
                sheetName: firstSheetName,
                fileSizeMB: (selectedFile.size / (1024 * 1024)).toFixed(2),
                headers: headerRow.slice(0, 17),
                sampleRows: sampleRows.map((r) => r.slice(0, 17)),
                hasRequiredColumns: headerRow.length >= 9
            });
        } catch (err) {
            console.warn('Ön izleme okunamadı:', err);
            setPreview(null);
        } finally {
            setParsingPreview(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setResult(null);
            setError(null);
            setProgress(null);
            inspectExcelFile(selectedFile);
        }
    };

    const handleClearFile = () => {
        setFile(null);
        setPreview(null);
        setError(null);
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Lütfen bir Excel (.xlsx) dosyası seçin.');
            return;
        }

        setUploading(true);
        setError(null);
        setResult(null);
        setProgress({
            isRunning: true,
            status: 'Queued',
            progressPercent: 0,
            processedRows: 0,
            totalRows: 0,
            message: 'Dosya sunucuya yükleniyor…'
        });

        const formData = new FormData();
        formData.append('file', file);
        formData.append('updateExisting', String(updateExisting));
        formData.append('createUserAccounts', String(createUserAccounts));
        formData.append('mustChangePassword', String(mustChangePassword));
        formData.append('syncToDevices', String(syncToDevices));

        if (defaultRoleName) formData.append('defaultRoleName', defaultRoleName);
        if (defaultAccessGroupId) formData.append('defaultAccessGroupId', defaultAccessGroupId);
        if (defaultWorkingRegimeId) formData.append('defaultWorkingRegimeId', defaultWorkingRegimeId);
        if (defaultLocationId) formData.append('defaultLocationId', defaultLocationId);

        try {
            const response = await fetch(`${BASE_URL}/Employee/import/excel`, {
                method: 'POST',
                headers: authHeaders(),
                body: formData
            });

            if (response.status === 409) {
                const conflict = await response.json();
                if (conflict?.runId) {
                    setError('Devam eden bir aktarım var; mevcut ilerleme gösteriliyor.');
                    applyStatus(conflict.status || { isRunning: true, runId: conflict.runId });
                    startPolling(conflict.runId);
                    return;
                }
                throw new Error(conflict?.message || 'Aktarım çakışması.');
            }

            if (!response.ok) {
                let errorMsg = 'Yükleme başarısız.';
                try {
                    const errData = await response.json();
                    errorMsg = errData.message || errData.title || errorMsg;
                } catch {
                    errorMsg = await response.text();
                }
                throw new Error(errorMsg || `Hata: ${response.status}`);
            }

            const data = await response.json();
            if (data?.status) applyStatus(data.status);
            if (data?.runId) startPolling(data.runId);
            else throw new Error('Aktarım kimliği alınamadı.');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Yükleme sırasında bir hata oluştu.');
            setUploading(false);
            stopPolling();
        }
    };

    const handleCancelImport = async () => {
        if (!progress?.runId) return;
        setCancelling(true);
        try {
            const res = await fetch(`${BASE_URL}/Employee/import/excel/cancel/${progress.runId}`, {
                method: 'POST',
                headers: authHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                applyStatus(data);
            }
        } catch (err) {
            console.error('İptal hatası:', err);
        } finally {
            setCancelling(false);
        }
    };

    // Hatalı satırları düzenlenebilir ve doğrudan tekrar yüklenebilir Akıllı Excel (.xlsx) olarak indir
    const downloadEditableErrorsExcel = async () => {
        if (!result) return;
        setDownloadingErrorsExcel(true);
        try {
            if (result.runId) {
                const res = await fetch(`${BASE_URL}/Employee/import/excel/errors-excel/${result.runId}`, {
                    headers: authHeaders()
                });
                if (res.ok) {
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `PDKS_Hatali_Personeller_Duzenle_Yukle_${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    return;
                }
            }

            // Fallback: İstemci tarafında 17 sütunlu şablon + 18. Hata Sütunu olarak oluştur
            const headers = [...TEMPLATE_HEADERS, 'Hata / Uyarı Açıklaması'];
            const rows =
                result.detailedErrors && result.detailedErrors.length > 0
                    ? result.detailedErrors.map((item) => {
                          const raw = Array.isArray(item.rowValues) && item.rowValues.length > 0
                              ? Array.from({ length: 17 }, (_, i) => item.rowValues[i] ?? '')
                              : [
                                    item.fullName?.split(' ')[0] || '',
                                    item.fullName?.split(' ').slice(1).join(' ') || '',
                                    item.tckn || '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    item.sicilNo || '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    '',
                                    ''
                                ];
                          return [...raw, `Satır #${item.rowNumber}: ${item.reason || ''}`];
                      })
                    : (result.errors || []).map((errText) => [...Array(17).fill(''), errText]);

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            ws['!cols'] = [...TEMPLATE_HEADERS.map(() => ({ wch: 18 })), { wch: 52 }];

            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                if (!ws[cellAddress]) continue;
                ws[cellAddress].s = {
                    fill: { fgColor: { rgb: C === 17 ? 'FEE2E2' : 'F1F5F9' } },
                    font: { name: 'Segoe UI', color: { rgb: C === 17 ? '991B1B' : '334155' }, bold: true, sz: 10 },
                    alignment: { horizontal: 'left', vertical: 'center' },
                    border: {
                        top: { style: 'thin', color: { rgb: 'CBD5E1' } },
                        bottom: { style: 'medium', color: { rgb: '94A3B8' } },
                        left: { style: 'thin', color: { rgb: 'CBD5E1' } },
                        right: { style: 'thin', color: { rgb: 'CBD5E1' } }
                    }
                };
            }

            XLSX.utils.book_append_sheet(wb, ws, 'Personel Listesi');
            XLSX.writeFile(wb, `PDKS_Hatali_Personeller_Duzenle_Yukle_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } finally {
            setDownloadingErrorsExcel(false);
        }
    };

    const filteredDetailedErrors = useMemo(() => {
        if (!result) return [];
        const list =
            result.detailedErrors && result.detailedErrors.length > 0
                ? result.detailedErrors
                : (result.errors || []).map((msg, i) => ({
                      rowNumber: i + 1,
                      tckn: '-',
                      fullName: '-',
                      sicilNo: '-',
                      reason: msg
                  }));

        if (!errorSearch.trim()) return list;
        const q = errorSearch.trim().toLocaleLowerCase('tr-TR');
        return list.filter(
            (x) =>
                String(x.rowNumber).includes(q) ||
                (x.tckn && x.tckn.toLocaleLowerCase('tr-TR').includes(q)) ||
                (x.fullName && x.fullName.toLocaleLowerCase('tr-TR').includes(q)) ||
                (x.sicilNo && x.sicilNo.toLocaleLowerCase('tr-TR').includes(q)) ||
                (x.reason && x.reason.toLocaleLowerCase('tr-TR').includes(q))
        );
    }, [result, errorSearch]);

    const percent = progress?.progressPercent ?? 0;
    const etaText =
        progress?.etaSeconds != null && progress.etaSeconds > 0
            ? progress.etaSeconds >= 60
                ? `~${Math.floor(progress.etaSeconds / 60)} dk ${progress.etaSeconds % 60} sn`
                : `~${progress.etaSeconds} sn`
            : null;

    return (
        <div className="space-y-5 p-1">
            {/* Üst Başlık ve Akıllı Şablon İndirme */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-700/70">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <FileSpreadsheet className="text-emerald-600 dark:text-emerald-400" size={24} />
                            Excel ile Toplu Personel Aktarımı
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                            20.000–30.000+ personeli Argon2id şifreli kullanıcı hesapları, açılır listeli (seçmeli) Excel doğrulaması ve kapı/slot yetkileriyle birlikte toplu aktarın.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={downloadTemplate}
                        disabled={downloadingTemplate}
                        className="flex items-center self-start sm:self-auto px-4 py-2.5 text-xs sm:text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors shrink-0 disabled:opacity-60"
                    >
                        {downloadingTemplate ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <FileDown className="w-4 h-4 mr-2" />
                        )}
                        Akıllı Excel Şablonunu İndir (Seçmeli .xlsx)
                    </button>
                </div>

                {/* Aktarım Seçenekleri (Kurumsal Konfigürasyon) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                    {/* 1. Mevcut Personel Davranışı */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                            <RefreshCw size={14} className="text-blue-500" />
                            Mevcut Personel (TCKN) Davranışı
                        </div>
                        <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-700 dark:text-gray-300">
                            <input
                                type="checkbox"
                                checked={updateExisting}
                                onChange={(e) => setUpdateExisting(e.target.checked)}
                                disabled={uploading}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>
                                <strong>Kayıtlı Personelleri Güncelle (Upsert):</strong> TCKN sistemde zaten varsa hata vermek yerine departman, pozisyon, lokasyon, kart ve yetki grubunu günceller.
                            </span>
                        </label>
                    </div>

                    {/* 2. Otomatik Kullanıcı & Argon2id Şifreleme */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                            <ShieldCheck size={14} className="text-emerald-500" />
                            Kullanıcı Hesabı & Argon2id Güvenliği
                        </div>
                        <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-700 dark:text-gray-300">
                            <input
                                type="checkbox"
                                checked={createUserAccounts}
                                onChange={(e) => setCreateUserAccounts(e.target.checked)}
                                disabled={uploading}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>
                                <strong>Otomatik Kullanıcı Aç (Argon2id):</strong> Yeni personeller için TCKN ile giriş hesabı oluşturur.
                            </span>
                        </label>
                        {createUserAccounts && (
                            <label className="flex items-center gap-2 pl-6 cursor-pointer text-xs text-gray-600 dark:text-gray-400">
                                <input
                                    type="checkbox"
                                    checked={mustChangePassword}
                                    onChange={(e) => setMustChangePassword(e.target.checked)}
                                    disabled={uploading}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span>İlk girişte şifre değiştirmeyi zorunlu tut</span>
                            </label>
                        )}
                    </div>

                    {/* 3. Cihaz & Panel Slotu Senkronizasyonu */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                            <Cpu size={14} className="text-indigo-500" />
                            Cihaz & Okuyucu Slot Senkronizasyonu
                        </div>
                        <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-700 dark:text-gray-300">
                            <input
                                type="checkbox"
                                checked={syncToDevices}
                                onChange={(e) => setSyncToDevices(e.target.checked)}
                                disabled={uploading}
                                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>
                                <strong>Cihazlara Otomatik Gönder:</strong> Kart No ve Yetki Grubu dolu olan personelleri kapı/panel okuyucu slotlarıyla birlikte cihaz kuyruğuna ekler.
                            </span>
                        </label>
                    </div>
                </div>

                {/* Excel'de Boş Bırakılan / "Belirtilmedi" Alanlar İçin Varsayılan Seçimler */}
                <div className="mb-6 p-4 rounded-xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/70 dark:border-blue-800/60">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-800 dark:text-blue-300 mb-1">
                        <Sliders size={14} className="text-blue-600 dark:text-blue-400" />
                        Excel&apos;de Boş Bırakılan veya &ldquo;Belirtilmedi&rdquo; Seçilen Alanlar İçin Varsayılan Değerler
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                        Excel şablonunda ilgili hücre boş bırakıldığında veya açılır listeden <strong>&ldquo;Belirtilmedi&rdquo;</strong> seçildiğinde uygulanacak varsayılan tanımları buradan seçebilirsiniz. Yanlış yazılan yetki grubu, çalışma rejimi veya kan grubu değerleri ise sistemde bozuk kayıt açmamak için reddedilir.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {/* Varsayılan Rol */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Varsayılan Sistem Rolü
                            </label>
                            <select
                                value={defaultRoleName}
                                onChange={(e) => setDefaultRoleName(e.target.value)}
                                disabled={uploading}
                                className="w-full px-2.5 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Otomatik (Personel Rolü)</option>
                                {lookups.roles.map((r) => (
                                    <option key={r.id} value={r.name}>
                                        {r.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Varsayılan Yetki Grubu */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Varsayılan Yetki Grubu
                            </label>
                            <select
                                value={defaultAccessGroupId}
                                onChange={(e) => setDefaultAccessGroupId(e.target.value)}
                                disabled={uploading}
                                className="w-full px-2.5 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Atama Yapma (Boş Bırak)</option>
                                {lookups.accessGroups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.name} ({g.deviceCount ?? 0} Kapı/Cihaz)
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Varsayılan Çalışma Rejimi */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Varsayılan Çalışma Rejimi
                            </label>
                            <select
                                value={defaultWorkingRegimeId}
                                onChange={(e) => setDefaultWorkingRegimeId(e.target.value)}
                                disabled={uploading}
                                className="w-full px-2.5 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Atama Yapma (Boş Bırak)</option>
                                {lookups.workingRegimes.map((w) => (
                                    <option key={w.id} value={w.id}>
                                        {w.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Varsayılan Lokasyon */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Varsayılan Lokasyon
                            </label>
                            <select
                                value={defaultLocationId}
                                onChange={(e) => setDefaultLocationId(e.target.value)}
                                disabled={uploading}
                                className="w-full px-2.5 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Atama Yapma (Boş Bırak)</option>
                                {lookups.locations.map((l) => (
                                    <option key={l.id} value={l.id}>
                                        {l.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Dosya Seçim Alanı */}
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-7 text-center hover:bg-gray-50/70 dark:hover:bg-gray-700/30 transition-colors">
                    <input
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileChange}
                        className="hidden"
                        id="excel-upload"
                        disabled={uploading}
                    />
                    <label
                        htmlFor="excel-upload"
                        className={`flex flex-col items-center ${
                            uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                    >
                        <Upload className="w-11 h-11 text-blue-500 mb-3" />
                        <span className="text-base sm:text-lg text-gray-800 dark:text-gray-200 font-semibold">
                            {file ? file.name : 'Excel (.xlsx) dosyasını seçmek için tıklayın'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Sadece <strong>.xlsx</strong> (Excel Çalışma Kitabı) formatı • Maksimum 100 MB (30.000+ satır destekli)
                        </span>
                    </label>
                </div>

                {/* Seçilen Dosya Ön İzleme (İlk 5 Satır) */}
                {parsingPreview && (
                    <div className="mt-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                        Excel dosyası kontrol ediliyor…
                    </div>
                )}

                {preview && file && !uploading && (
                    <div className="mt-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200">
                                <Eye size={16} className="text-blue-500" />
                                <span>Dosya Ön İzleme ({preview.sheetName})</span>
                                <span className="px-2 py-0.5 text-[11px] rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                    {preview.fileSizeMB} MB
                                </span>
                                {!preview.hasRequiredColumns && (
                                    <span className="px-2 py-0.5 text-[11px] rounded-full bg-amber-100 text-amber-800">
                                        ⚠️ Sütun sayısı şablondan az görünüyor, lütfen kontrol edin
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleClearFile}
                                className="text-xs text-rose-600 dark:text-rose-400 hover:underline flex items-center gap-1"
                            >
                                <Trash2 size={13} /> Dosyayı Kaldır
                            </button>
                        </div>

                        {preview.sampleRows.length > 0 && (
                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                    <thead className="bg-gray-100 dark:bg-gray-800">
                                        <tr>
                                            {preview.headers.slice(0, 9).map((h, idx) => (
                                                <th
                                                    key={idx}
                                                    className="px-2.5 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap"
                                                >
                                                    {h || `Sütun ${idx + 1}`}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800/80">
                                        {preview.sampleRows.map((r, rIdx) => (
                                            <tr key={rIdx}>
                                                {preview.headers.slice(0, 9).map((_, cIdx) => (
                                                    <td
                                                        key={cIdx}
                                                        className="px-2.5 py-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap"
                                                    >
                                                        {String(r[cIdx] ?? '-')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {file && !uploading && (
                    <div className="mt-4 flex justify-end">
                        <button
                            type="button"
                            onClick={handleUpload}
                            disabled={uploading}
                            className="flex items-center px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                        >
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Aktarımı Başlat
                        </button>
                    </div>
                )}

                {/* Canlı İlerleme (Progress) ve İptal Butonu */}
                {(uploading || (progress && progress.isRunning)) && (
                    <div className="mt-6 p-5 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/70 dark:bg-indigo-900/20 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                            <div className="flex items-center gap-2 font-semibold text-indigo-900 dark:text-indigo-200">
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-600 dark:text-indigo-400" />
                                <span>{progress?.message || 'Aktarım devam ediyor…'}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-bold text-indigo-700 dark:text-indigo-300 tabular-nums">
                                    %{percent}
                                </span>
                                {progress?.runId && (
                                    <button
                                        type="button"
                                        onClick={handleCancelImport}
                                        disabled={cancelling}
                                        className="px-3 py-1 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-md flex items-center gap-1 shadow-sm disabled:opacity-50"
                                    >
                                        <StopCircle size={14} />
                                        {cancelling ? 'Durduruluyor…' : 'Aktarımı Durdur'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="h-3 rounded-full bg-indigo-100 dark:bg-indigo-950 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-indigo-600 transition-all duration-500 ease-out"
                                style={{ width: `${Math.max(2, percent)}%` }}
                            />
                        </div>

                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-indigo-800/90 dark:text-indigo-300/90 font-medium">
                            <span>
                                İşlenen: <strong>{(progress?.processedRows ?? 0).toLocaleString('tr-TR')}</strong> /{' '}
                                <strong>{(progress?.totalRows ?? 0).toLocaleString('tr-TR')}</strong> satır
                            </span>
                            <span className="text-emerald-700 dark:text-emerald-400">
                                Yeni Eklenen: <strong>{(progress?.createdCount ?? progress?.successCount ?? 0).toLocaleString('tr-TR')}</strong>
                            </span>
                            {(progress?.updatedCount ?? 0) > 0 && (
                                <span className="text-blue-700 dark:text-blue-400">
                                    Güncellenen: <strong>{progress.updatedCount.toLocaleString('tr-TR')}</strong>
                                </span>
                            )}
                            <span className="text-rose-700 dark:text-rose-400">
                                Hatalı / Atlanan: <strong>{(progress?.failedCount ?? 0).toLocaleString('tr-TR')}</strong>
                            </span>
                            {etaText && <span>Tahmini Kalan Süre: <strong>{etaText}</strong></span>}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 flex items-start text-sm">
                        <AlertTriangle className="w-5 h-5 mr-2.5 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
            </div>

            {/* İşlem Sonucu ve Hata Raporu */}
            {result && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-700/70 space-y-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                {result.status === 'Cancelled' ? (
                                    <span className="text-amber-600">⚠️ Aktarım Kullanıcı Tarafından Durduruldu</span>
                                ) : (
                                    <span>Aktarım Sonuç Özeti</span>
                                )}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Toplam {(result.totalRows || result.successCount + result.failedCount).toLocaleString('tr-TR')} satır işlendi.
                            </p>
                        </div>

                        {(result.failedCount > 0 || (result.errors && result.errors.length > 0)) && (
                            <button
                                type="button"
                                onClick={downloadEditableErrorsExcel}
                                disabled={downloadingErrorsExcel}
                                className="flex items-center self-start sm:self-auto px-4 py-2.5 text-xs sm:text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded-lg shadow-sm transition-colors disabled:opacity-60"
                            >
                                {downloadingErrorsExcel ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <FileDown className="w-4 h-4 mr-2" />
                                )}
                                Hatalı Kişileri Düzenlenebilir Excel Olarak İndir (Düzenle &amp; Tekrar Yükle)
                            </button>
                        )}
                    </div>

                    {/* 4'lü KPI Sonuç Kartları */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center gap-3">
                            <div className="p-2.5 bg-emerald-100 dark:bg-emerald-800 rounded-full">
                                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-300" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Yeni Eklenen Personel</p>
                                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                                    {(result.createdCount ?? result.successCount ?? 0).toLocaleString('tr-TR')}
                                </p>
                            </div>
                        </div>

                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-center gap-3">
                            <div className="p-2.5 bg-blue-100 dark:bg-blue-800 rounded-full">
                                <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Güncellenen Personel</p>
                                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                                    {(result.UpdatedCount ?? 0).toLocaleString('tr-TR')}
                                </p>
                            </div>
                        </div>

                        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl flex items-center gap-3">
                            <div className="p-2.5 bg-indigo-100 dark:bg-indigo-800 rounded-full">
                                <Cpu className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Cihaz Kuyruğu Komutu</p>
                                <p className="text-xl font-bold text-indigo-700 dark:text-indigo-300">
                                    {(result.deviceCommandsQueued ?? 0).toLocaleString('tr-TR')}
                                </p>
                            </div>
                        </div>

                        <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl flex items-center gap-3">
                            <div className="p-2.5 bg-rose-100 dark:bg-rose-800 rounded-full">
                                <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-300" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Hatalı / Atlanan Satır</p>
                                <p className="text-xl font-bold text-rose-700 dark:text-rose-300">
                                    {(result.failedCount ?? 0).toLocaleString('tr-TR')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Detaylı Hata Tablosu */}
                    {filteredDetailedErrors.length > 0 && (
                        <div className="space-y-3 pt-2">
                            {/* Hızlı Düzelt & Tekrar Yükle Rehber Kutusu */}
                            <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/70 flex items-start gap-2.5 text-xs text-amber-900 dark:text-amber-200">
                                <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                <div>
                                    <strong>Hızlı Düzelt &amp; Tekrar Yükle Özelliği:</strong> Sağ üstteki{' '}
                                    <strong>&ldquo;Hatalı Kişileri Düzenlenebilir Excel Olarak İndir&rdquo;</strong> butonuna tıkladığınızda, yalnızca bu hatalı personellerin tüm bilgileriyle yer aldığı <strong>açılır listeli (seçmeli) özel bir Excel şablonu</strong> iner. En sağdaki kırmızı <strong>&ldquo;HATA / UYARI NEDENİ&rdquo;</strong> sütununa bakarak hatalı hücreleri Excel içindeki açılır listeden düzeltip, <strong>hiçbir sütunu silmeden aynı dosyayı</strong> doğrudan yukarıdan tekrar yükleyebilirsiniz.
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <h4 className="text-sm font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
                                    <AlertTriangle className="w-4 h-4" />
                                    Hata ve Atlanan Satır Detayları ({filteredDetailedErrors.length.toLocaleString('tr-TR')} kayıt)
                                </h4>

                                <div className="relative w-full sm:w-72">
                                    <input
                                        type="text"
                                        value={errorSearch}
                                        onChange={(e) => setErrorSearch(e.target.value)}
                                        placeholder="TCKN, ad soyad veya hata ara..."
                                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                    <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 w-24">
                                                Satır No
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 w-36">
                                                TCKN
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 w-48">
                                                Ad Soyad
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">
                                                Hata / Atlanma Nedeni
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                        {filteredDetailedErrors.slice(0, 300).map((item, index) => (
                                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-3 py-2 font-mono text-gray-500 dark:text-gray-400">
                                                    #{item.rowNumber}
                                                </td>
                                                <td className="px-3 py-2 font-mono text-gray-800 dark:text-gray-200">
                                                    {item.tckn || '-'}
                                                </td>
                                                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">
                                                    {item.fullName || '-'}
                                                </td>
                                                <td className="px-3 py-2 text-rose-600 dark:text-rose-400">
                                                    {item.reason}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
